import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { ReactComponent as ColumnList } from "../../image/ColumnList.svg";
import { Trans, useTranslation } from "react-i18next";
import {
  Space,
  Select,
  DatePicker,
  Table,
  Input,
  Modal,
  Col,
  Form,
  Row,
  Tooltip,
  Checkbox,
  Menu,
  Button,
  Dropdown,
  Popover,
  Breadcrumb,
} from "antd";
import ButtonField from "../../common/htmlComponents/ButtonField";
import RefreshIcon from "../../image/refreshIcon.svg";
import SearchIcon from "../../image/SearchIcon.svg";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";
import { getRequest } from "../../axios";
import moment from "moment";
import { authUserData } from "../../utils/Helper";
import {
  EyeOutlined,
  FilterOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import SubscriptionNotification from "../../authentication/SubscriptionNotification";

const SiteWiseReport = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const CompanyId = authUserData()?.company ? authUserData()?.company : null;
  const [reload, setReload] = useState(false);
  const { Search } = Input;
  const [totalCount, settotalCount] = useState(0);
  const [siteWiseData, setSiteWiseData] = useState([]);
  const [selectedRow, setSelectedRow] = useState([]);
  const formRef = useRef();
  const [clickedFilterButton, setClickedFilterButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [filterMaterialName, setFilterMaterialName] = useState("");
  const [filterSiteId, setFilterSiteId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [siteList, setSiteList] = useState([]);
  const [materialList, setMaterialList] = useState([]);

  const [checkedList, setCheckedList] = useState([
    "material_name",
    "available_quantity",
    "distributed_quantity",
    "total_quantity",
    "uom",
    "site_name",
    "status",
  ]);

  const [state, setState] = useState({
    page: 1,
    ordering: "id",
    pagesize: 10,
    material_name: "",
    site_id: "",
  });

  const debounceLoadData = useRef(
    debounce((value) => {
      if (value) {
        setState((prevState) => ({
          ...prevState,
          page: 1,
          search: value,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          page: 1,
          search: "",
        }));
      }
    }, 1000)
  );

  const getSiteWiseReport = async () => {
    try {
      setLoading(true);

      let queryParams = new URLSearchParams();
      if (state.material_name) {
        queryParams.append("material_name", state.material_name);
      }
      if (state.site_id) {
        queryParams.append("site_id", state.site_id);
      }
      if (state.created_at__gte) {
        queryParams.append("created_at__gte", state.created_at__gte);
      }
      if (state.created_at__lte) {
        queryParams.append("created_at__lte", state.created_at__lte);
      }
      if (state.search) {
        queryParams.append("search", state.search);
      }
      queryParams.append("page", state.page);
      queryParams.append("page_size", state.pagesize);
      queryParams.append("ordering", state.ordering);

      let apiResponse = await getRequest(
        `material-inventory/?${queryParams.toString()}`
      );

      if (apiResponse.data.results?.data) {
        setSiteWiseData(apiResponse.data.results.data);
        settotalCount(apiResponse.data.count);
      } else if (apiResponse.data.data) {
        setSiteWiseData(apiResponse.data.data);
        settotalCount(apiResponse.data.count || apiResponse.data.data.length);
      } else {
        ErrorNotificationMsg(t("error_in_site_wise_report"));
      }
      setLoading(false);
    } catch (error) {
      ErrorNotificationMsg(t("error_in_site_wise_report"));
      setLoading(false);
    }
  };

  const getSiteList = async () => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", "1");
      queryParams.append("ordering", "id");
      queryParams.append("pagesize", "10");

      const response = await getRequest(
        `delivery-address/${CompanyId}/get-address-by-company/?${queryParams.toString()}`
      );
      if (response?.data?.results?.data) {
        setSiteList(response.data.results.data);
      } else {
        setSiteList([]);
      }
    } catch (error) {
      console.error("Error fetching site list:", error);
      setSiteList([]);
    }
  };

  const getMaterialList = async () => {
    try {
      const response = await getRequest("material-inventory/material-names/");
      if (response?.data?.data) {
        setMaterialList(response.data.data);
      } else {
        setMaterialList([]);
      }
    } catch (error) {
      console.error("Error fetching material list:", error);
      setMaterialList([]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [];
        promises.push(getSiteWiseReport());
        promises.push(getSiteList());
        promises.push(getMaterialList());

        await Promise.all(promises);
      } catch (error) {
        console.error("Error in fetchData:", error);
      }
    };

    fetchData();
  }, [reload, state, t]);

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
  };

  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedRow(selectedRowKeys);
    },
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "site_name" && sortOrder === "ascend") {
      sortingEnumKey = "site_name";
    }
    if (sortField === "site_name" && sortOrder === "descend") {
      sortingEnumKey = "-site_name";
    }
    if (sortField === "material_name" && sortOrder === "ascend") {
      sortingEnumKey = "material_name";
    }
    if (sortField === "material_name" && sortOrder === "descend") {
      sortingEnumKey = "-material_name";
    }
    if (sortField === "quantity" && sortOrder === "ascend") {
      sortingEnumKey = "quantity";
    }
    if (sortField === "quantity" && sortOrder === "descend") {
      sortingEnumKey = "-quantity";
    }

    return sortingEnumKey;
  };

  const handleReset = () => {
    setClickedFilterButton(false);
    setFilterMaterialName("");
    setFilterSiteId("");
    setStartDate("");
    setEndDate("");
    debounceLoadData.current.cancel();

    formRef.current?.setFieldsValue({
      material_name: null,
      site_id: null,
      start_date: null,
      end_date: null,
      search: "",
    });

    setState({
      page: 1,
      ordering: "id",
      pagesize: 10,
      material_name: "",
      site_id: "",
      created_at__gte: "",
      created_at__lte: "",
      search: "",
    });
    setReload((prev) => !prev);
  };

  const handleClearFields = () => {
    setFilterMaterialName("");
    setFilterSiteId("");
    setStartDate("");
    setEndDate("");
    formRef.current?.setFieldsValue({
      material_name: null,
      site_id: null,
      start_date: null,
      end_date: null,
      search: "",
    });
  };

  const handleRefresh = async () => {
    setClickedFilterButton(false);
    setFilterMaterialName("");
    setFilterSiteId("");
    setStartDate("");
    setEndDate("");
    formRef.current.setFieldsValue({
      material_name: null,
      site_id: null,
      start_date: null,
      end_date: null,
      search: "",
    });
    setState({
      page: 1,
      ordering: "id",
      pagesize: 10,
      material_name: "",
      site_id: "",
      created_at__gte: "",
      created_at__lte: "",
    });
  };

  const handleStartDateChange = (date) => {
    const formattedDate = moment(date).format("DD-MM-YY");
    setStartDate(formattedDate);
  };

  const handleEndDateChange = (date) => {
    const formattedDate = moment(date).format("DD-MM-YY");
    setEndDate(formattedDate);
  };

  const handleApplyFilter = () => {
    setClickedFilterButton(true);
    setState({
      ...state,
      page: 1,
      material_name: filterMaterialName,
      site_id: filterSiteId,
      created_at__gte: startDate,
      created_at__lte: endDate,
    });
  };

  const handleColumnVisibilityChange = (checkedValues) => {
    setCheckedList(checkedValues);
  };

  const columns = [
    {
      title: "Item Name",
      dataIndex: "material_name",
      key: "material_name",
      sorter: true,
      ellipsis: true,
      width: 200,
    },
    {
      title: "Available Qty.",
      dataIndex: "available_quantity",
      key: "available_quantity",
      sorter: true,
      align: "center",
      width: 140,
    },
    {
      title: "Distributed Qty.",
      dataIndex: "distributed_quantity",
      key: "distributed_quantity",
      sorter: true,
      align: "center",
      width: 150,
    },
    {
      title: "Total Qty.",
      dataIndex: "total_quantity",
      key: "total_quantity",
      sorter: true,
      align: "center",
      width: 120,
    },
    {
      title: "UoM.",
      dataIndex: "uom",
      key: "uom",
      sorter: true,
      width: 80,
    },
    {
      title: "Location",
      dataIndex: "site_name",
      key: "site_name",
      sorter: true,
      ellipsis: true,
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      ellipsis: true,
      width: 100,
    },
    {
      title: "Item Specification",
      dataIndex: "item_specification",
      key: "item_specification",
      sorter: true,
      ellipsis: true,
      width: 250,
    },
    {
      title: "GRN Number",
      dataIndex: "grn_number",
      key: "grn_number",
      sorter: true,
      ellipsis: true,
      width: 120,
    },
    {
      title: "Invoice Number",
      dataIndex: "invoice_number",
      key: "invoice_number",
      sorter: true,
      ellipsis: true,
      width: 130,
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      width: 120,
    },
  ];

  const filteredColumns = columns.filter((column) =>
    checkedList?.includes(column.key)
  );

  const columnMenu = (
    <Menu
      className="packagelistdropdown"
      style={{ width: "450px", padding: "10px", borderRadius: "8px" }}
    >
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={handleColumnVisibilityChange}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Row style={{ width: "100%" }}>
            <Col span={12}>
              <Checkbox value="material_name">Item Name</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="available_quantity">Available Qty.</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="distributed_quantity">Distributed Qty.</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="total_quantity">Total Qty.</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="uom">UoM.</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="site_name">Location</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="status">Status</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="item_specification">Item Specification</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="grn_number">GRN Number</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="invoice_number">Invoice Number</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="created_at">Created Date</Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  useEffect(() => {
    return () => {
      debounceLoadData.current.cancel();
    };
  }, []);

  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData.current(value);
  };

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>Available Inventory</h3>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Dashboard</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Available Inventory</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
      </div>
      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          <SubscriptionNotification />

          <section className="Block-Sec-Demo1 mb-5">
            <div className="table-sec">
        

              <div className="filterrow">
                <div className="filtercol">
                  <Form autoComplete="off" ref={formRef} form={form}>
                    <Space wrap>
                      <Col xs={24} className="TextFeild gutter-row">
                        <Form.Item name="search" initialValue="" style={{ margin: 0 }}>
                          <Search
                            prefix={
                              <img
                                src={SearchIcon}
                                alt="SearchIcon"
                                className="SearchIcon"
                                width={22}
                              />
                            }
                            placeholder="Search by Item Code, Name, Specific Detail"
                            onChange={searchCallbackDelayed}
                            style={{ width: 450 }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} className="TextFeild gutter-row">
                        <Form.Item name="site_id">
                          <Select
                            allowClear
                            placeholder="Location"
                            value={filterSiteId}
                            onChange={(value) => setFilterSiteId(value)}
                            style={{ minWidth: 180 , }}
                            optionFilterProp="children"
                          >
                            {siteList.map((site) => (
                              <Select.Option key={site.id} value={site.id}>
                                {site.site_name || site.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} className="TextFeild gutter-row">
                        <Form.Item name="start_date">
                          <DatePicker
                            format="DD-MM-YY"
                            allowClear={false}
                            placeholder="Select Start Date"
                            value={startDate ? moment(startDate, "DD-MM-YY") : null}
                            onChange={handleStartDateChange}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} className="TextFeild gutter-row">
                        <Form.Item name="end_date">
                          <DatePicker
                            format="DD-MM-YY"
                            allowClear={false}
                            placeholder="Select End Date"
                            value={endDate ? moment(endDate, "DD-MM-YY") : null}
                            onChange={handleEndDateChange}
                          />
                        </Form.Item>
                      </Col>
                      
                      
                      <>
                        <Button
                          type="primary"
                          htmlType="Apply"
                          icon={
                            <Tooltip title="Apply" color="#094E8F">
                              <FilterOutlined style={{ fontSize: "20px" }} />
                            </Tooltip>
                          }
                          style={{
                            backgroundColor: "#094e8f",
                            color: "#ffffff",
                            border: "none",
                            width: "40px",
                            height: "40px",
                            boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                            borderRadius: "4px",
                          }}
                          onClick={
                            startDate || endDate || filterSiteId || filterMaterialName
                              ? handleApplyFilter
                              : null
                          }
                        />

                        <Button
                          htmlType="button"
                          type="primary"
                          icon={
                            <Tooltip title="Reset" color="#094E8F">
                              <UndoOutlined style={{ fontSize: "20px" }} />
                            </Tooltip>
                          }
                          onClick={
                            clickedFilterButton || state.search
                              ? handleReset
                              : handleClearFields
                          }
                          style={{
                            backgroundColor: "#094e8f",
                            color: "#ffffff",
                            border: "none",
                            width: "40px",
                            height: "40px",
                            boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                            borderRadius: "4px",
                          }}
                        />
                      </>
                    </Space>
                  </Form>
                </div>

                <div className="btnscol">
                  <Space>
                    
                    <div>
                      <Space
                        style={{
                          backgroundColor: "#094e8f",
                          padding: "4px",
                          borderRadius: "4px",
                        }}
                      >
                        <Dropdown
                          overlay={columnMenu}
                          trigger={["hover"]}
                          placement="bottomRight"
                          visible={dropdownVisible}
                          onVisibleChange={setDropdownVisible}
                        >
                          <Tooltip
                            title="Modify Columns"
                            placement="topLeft"
                            color="#094E8F"
                          >
                            <Button
                              className="SaveGradiantBtn"
                              type="primary"
                              icon={<ColumnList />}
                              style={{ minWidth: "36px" }}
                            />
                          </Tooltip>
                        </Dropdown>
                      </Space>
                    </div>
                  </Space>
                </div>
              </div>
              <Table
                columns={filteredColumns}
                dataSource={siteWiseData}
                pagination={{
                  current: state.page,
                  pageSize: state.pagesize,
                  total: totalCount,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
                onChange={handleTableChange}
                loading={loading}
                // rowSelection={rowSelection}
                 scroll={{
                x: "auto",
              }}
                rowKey="material_id"
              />
            </div>
          </section>
        </div>
      </section>
    </>
  );
};

export default SiteWiseReport;
